import { useState, useEffect } from 'react';
import { Calendar, CheckCircle2, ChevronLeft, ChevronRight, Phone } from 'lucide-react';
import { fetchCollection } from '../../lib/cms';
import ContactForm from '../../components/ContactForm';
import SEO from '../../components/SEO';

export default function DemoEn() {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    async function loadData() {
      const testData = await fetchCollection('testimonials', 'en');
      setTestimonials(testData);
    }
    loadData();
  }, []);

  useEffect(() => {
    if (testimonials.length === 0 || isPaused) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials, isPaused]);

  const nextSlide = () => {
    if (testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    if (testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const fordele = [
    {
      title: 'Physical trial',
      desc: 'You get to test the machine in your own stable facility on your own horse boxes and arena footing.'
    },
    {
      title: 'Personal guidance',
      desc: 'Rasmus walks you through the machine and advises on optimal use for your specific stable environment.'
    },
    {
      title: '100% non-binding',
      desc: 'It costs nothing and there is zero purchase pressure. We simply want to demonstrate the value of the machine.'
    }
  ];

  const trin = [
    {
      step: '1',
      title: 'Book a time',
      desc: (
        <span>
          Fill out the form above, or call Rasmus directly at{' '}
          <a href="tel:25303589" className="text-[#0E67A2] hover:underline font-bold">
            +45 2530 3589
          </a>{' '}
          to schedule a date.
        </span>
      )
    },
    {
      step: '2',
      title: 'We come to you',
      desc: 'Rasmus arrives at your stable with the demonstration machine and all required accessories.'
    },
    {
      step: '3',
      title: 'Try it yourself',
      desc: 'You and your stable hands test the machine directly, experiencing the physical difference on your own body.'
    }
  ];

  const demoSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Free Demo of Danshaker',
    provider: {
      '@type': 'LocalBusiness',
      name: 'Danshaker',
    },
    description: 'Book a non-binding and free demonstration of Danshaker directly in your own stable.',
    areaServed: 'DK',
    isOfferCatalogOf: {
      '@type': 'OfferCatalog',
      name: 'Demonstration in own stable',
    },
  };

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-950 antialiased">
      <SEO
        title="Book Free Demo - Try in Your Stable"
        description="Book a free and non-binding demonstration of the Danshaker sorting machine directly in your own stable. Test the machine on your own boxes before deciding."
        path="/en/free-demo"
        lang="en"
        schema={demoSchema}
      />

      {/* Background gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,103,162,0.08),transparent_60%)]" />

      {/* Hero Section - Booking Form is the very first content */}
      <section className="mx-auto max-w-7xl px-4 pt-12 pb-16 md:px-8 md:pt-16">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-start">
          {/* Left Column: Info */}
          <div className="space-y-6 lg:col-span-6 order-2 lg:order-1 lg:sticky lg:top-40">
            <div className="inline-flex items-center gap-2 rounded-full border-2 border-[#0E67A2]/30 bg-[#0E67A2]/10 px-4 py-1.5 text-xs font-black text-[#0E67A2]">
              <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Free and non-binding trial</span>
            </div>
            <h1 className="text-4xl font-black tracking-tight text-slate-950 sm:text-5xl md:text-6xl leading-tight">
              Experience Danshaker<br />
              <span className="bg-gradient-to-r from-[#0E67A2] to-sky-700 bg-clip-text text-transparent">in your own stable</span>
            </h1>
            <p className="max-w-xl text-lg text-slate-900 font-semibold leading-relaxed">
              We visit your facility and demonstrate the Danshaker, giving you and your team full opportunity to test it on your own boxes. Completely free of commitment.
            </p>
            <p className="max-w-xl text-base text-slate-800 font-medium leading-relaxed">
              At this time, this is only available in Denmark.
            </p>
            
            <div className="rounded-xl border-2 border-[#0E67A2]/30 bg-white p-5 max-w-xl shadow-md">
              <p className="text-sm text-slate-950 font-bold">
                Want to test the machine over a longer period before deciding to invest?
              </p>
              <p className="mt-2 text-base text-[#0E67A2] font-black">
                Rent a demo machine for 14 days for only €135 excl. VAT + deposit.
              </p>
            </div>

            {/* Quick benefits list in hero */}
            <div className="space-y-3 pt-4 border-t border-slate-200">
              <h2 className="text-xs font-black text-slate-950 uppercase tracking-wider">Why book a demo?</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex items-center gap-2 text-sm text-slate-950 font-bold">
                  <span className="text-[#0E67A2] font-black text-lg" aria-hidden="true">✓</span>
                  <span>100% non-binding</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-950 font-bold">
                  <span className="text-[#0E67A2] font-black text-lg" aria-hidden="true">✓</span>
                  <span>Test on your own boxes</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-950 font-bold">
                  <span className="text-[#0E67A2] font-black text-lg" aria-hidden="true">✓</span>
                  <span>Personal expert guidance</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-950 font-bold">
                  <span className="text-[#0E67A2] font-black text-lg" aria-hidden="true">✓</span>
                  <span>Zero purchase obligation</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Booking Form */}
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="rounded-3xl border-2 border-[#0E67A2] bg-white p-6 md:p-8 shadow-2xl relative">
              <div className="absolute -top-3.5 left-8 bg-[#0E67A2] text-white px-4 py-1 rounded-full text-xs font-black uppercase tracking-wider">
                Book demo now
              </div>
              <h2 className="text-2xl font-black text-slate-950 mb-2">Fill in the form to book</h2>
              <p className="text-sm text-slate-800 font-semibold mb-6">
                We will contact you shortly to arrange a date and time that fits your schedule.
              </p>
              <ContactForm defaultService="Gratis demo" buttonText="Book free demo now" lang="en" />
            </div>
          </div>
        </div>
      </section>

      {/* Process / How it works */}
      <section className="border-t-2 border-slate-300 bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-black text-slate-950 md:text-4xl">How it works</h2>
            <p className="mt-4 text-slate-900 font-bold">3 simple steps to test the Danshaker in your stable.</p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            {trin.map((item) => (
              <div key={item.step} className="relative rounded-2xl border-2 border-slate-300 bg-slate-50 p-8 shadow-md">
                <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-[#0E67A2] text-xl font-black text-white">
                  {item.step}
                </div>
                <h3 className="text-xl font-black text-slate-950 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-900 font-semibold leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Detailed */}
      <section className="py-16 mx-auto max-w-7xl px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl font-black text-slate-950 md:text-4xl">Benefits of a live demonstration</h2>
        </div>

        <div className="grid gap-8 md:grid-cols-3">
          {fordele.map((item, idx) => (
            <div key={idx} className="rounded-2xl border-2 border-slate-300 bg-white p-8 shadow-md">
              <CheckCircle2 className="h-10 w-10 text-[#0E67A2] mb-4" aria-hidden="true" />
              <h3 className="text-xl font-black text-slate-950 mb-2">{item.title}</h3>
              <p className="text-sm text-slate-900 font-semibold leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="border-t-2 border-slate-300 bg-white py-16">
          <div className="mx-auto max-w-5xl px-4 md:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-black text-slate-950">What others say about the demo</h2>
            </div>

            <div 
              className="relative overflow-hidden rounded-3xl border-2 border-slate-300 bg-slate-50 p-8 md:p-12 shadow-lg"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  {testimonials[currentIndex]?.image && testimonials[currentIndex].image[0] ? (
                    <img
                      src={testimonials[currentIndex].image[0]}
                      alt={testimonials[currentIndex].name}
                      width="56"
                      height="56"
                      loading="lazy"
                      decoding="async"
                      className="h-14 w-14 rounded-full object-cover border border-slate-200"
                    />
                  ) : (
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#0E67A2]/10 text-[#0E67A2] font-black text-xl">
                      {testimonials[currentIndex]?.name ? testimonials[currentIndex].name.charAt(0) : 'K'}
                    </div>
                  )}
                  <div>
                    <h3 className="font-black text-slate-950 text-lg">{testimonials[currentIndex]?.name}</h3>
                    <p className="text-xs text-slate-700 font-bold">{testimonials[currentIndex]?.company}</p>
                  </div>
                </div>

                <div
                  className="text-base text-slate-900 leading-relaxed italic font-semibold"
                  dangerouslySetInnerHTML={{ __html: testimonials[currentIndex]?.text }}
                />
              </div>

              {testimonials.length > 1 && (
                <div className="flex items-center justify-between border-t-2 border-slate-200 pt-6 mt-8">
                  <div className="flex gap-2">
                    {testimonials.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentIndex(idx)}
                        className={`h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                          idx === currentIndex ? 'w-8 bg-[#0E67A2]' : 'w-2.5 bg-slate-300 hover:bg-slate-400'
                        }`}
                        aria-label={`Go to review ${idx + 1}`}
                      />
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={prevSlide}
                      className="p-2 rounded-xl border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 hover:text-[#0E67A2] transition shadow-xs cursor-pointer"
                      aria-label="Previous review"
                    >
                      <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                    </button>
                    <button
                      onClick={nextSlide}
                      className="p-2 rounded-xl border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 hover:text-[#0E67A2] transition shadow-xs cursor-pointer"
                      aria-label="Next review"
                    >
                      <ChevronRight className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Direct Contact Banner */}
      <section className="bg-slate-900 py-16 text-white text-center">
        <div className="mx-auto max-w-4xl px-4 md:px-8 space-y-6">
          <h2 className="text-3xl font-black sm:text-4xl">Prefer to arrange a date by phone?</h2>
          <p className="text-slate-300 text-lg font-medium">
            Call Rasmus directly – we are always happy to answer your questions.
          </p>
          <div>
            <a
              href="tel:25303589"
              className="inline-flex items-center gap-3 rounded-xl bg-[#0E67A2] px-8 py-4 font-black text-white shadow-lg transition hover:bg-[#0E67A2]/90 text-lg"
            >
              <Phone className="h-6 w-6" aria-hidden="true" />
              Call +45 2530 3589
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
